import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Skeleton,
  toast,
} from '@ponzinomics/ui';
import { cn } from '@ponzinomics/ui';
import {
  useBillingBalance,
  useCreditPackages,
  usePaymentHistory,
  useStripeCheckout,
  useSolanaPayment,
} from '../hooks/use-billing';
import type { CreditPackage, PaymentRecord, SolanaPaymentResult } from '../lib/billing-api';
import { SolanaPayModal } from './billing-solana-modal';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatCredits(n: number): string {
  return n.toLocaleString();
}

// ── Balance card ──────────────────────────────────────────────────────────────

function BalanceCard(): React.ReactElement {
  const { data: balance, isLoading } = useBillingBalance();

  const used = balance?.freeCreditsUsed ?? 0;
  const limit = balance?.freeCreditsLimit ?? 1000;
  const pct = Math.min(100, Math.round((used / limit) * 100));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Credit Balance</CardTitle>
        <CardDescription>Your current usage and prepaid credits</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-4 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-end justify-between">
              <div>
                <p className="text-sm text-gray-400">Prepaid Credits</p>
                <p className="text-4xl font-bold tabular-nums text-white">
                  {formatCredits(balance?.paidCredits ?? 0)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Total this period</p>
                <p className="text-lg font-semibold text-white">
                  {formatCredits(balance?.totalCallsThisPeriod ?? 0)} calls
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Free tier</span>
                <span className={cn('font-medium', pct >= 90 ? 'text-red-400' : 'text-gray-300')}>
                  {formatCredits(used)} / {formatCredits(limit)} used
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-white/[0.08]">
                <div
                  className={cn(
                    'h-2 rounded-full transition-all',
                    pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-brand-400'
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-xs text-gray-500">
                {pct < 100
                  ? `${formatCredits(limit - used)} free calls remaining this month`
                  : 'Free tier exhausted — prepaid credits are being used'}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Package card ──────────────────────────────────────────────────────────────

interface PackageCardProps {
  pkg: CreditPackage;
  onStripe: () => void;
  onSolana: () => void;
  stripeLoading: boolean;
  solanaLoading: boolean;
}

function PackageCard({
  pkg,
  onStripe,
  onSolana,
  stripeLoading,
  solanaLoading,
}: PackageCardProps): React.ReactElement {
  const busy = stripeLoading || solanaLoading;
  return (
    <Card className={cn('relative', pkg.discountPercent >= 20 && 'ring-brand-400/40 ring-1')}>
      {pkg.discountPercent > 0 && (
        <div className="absolute -top-2.5 right-4">
          <Badge className="bg-brand-400 font-semibold text-gray-950">
            {pkg.discountPercent}% off
          </Badge>
        </div>
      )}
      <CardHeader className="pb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{pkg.label}</p>
        <CardTitle className="text-2xl">{formatCredits(pkg.credits)} credits</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-white">${pkg.priceUsd.toFixed(2)}</span>
          {pkg.discountPercent > 0 && (
            <span className="text-sm text-gray-500 line-through">
              ${pkg.originalPriceUsd.toFixed(2)}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">
          ${((pkg.priceUsd / pkg.credits) * 1000).toFixed(2)} per 1,000 calls
        </p>
        <div className="flex flex-col gap-2">
          <Button className="w-full" onClick={onStripe} disabled={busy}>
            {stripeLoading ? 'Redirecting...' : 'Pay with Card'}
          </Button>
          <Button variant="outline" className="w-full" onClick={onSolana} disabled={busy}>
            {solanaLoading ? 'Preparing...' : 'Pay with USDC'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Payment history ───────────────────────────────────────────────────────────

function HistoryRow({ record }: { record: PaymentRecord }): React.ReactElement {
  return (
    <tr className="text-gray-300">
      <td className="py-3 pr-4 text-gray-400">{formatDate(record.date)}</td>
      <td className="py-3 pr-4 font-medium text-white">{formatCredits(record.credits)}</td>
      <td className="py-3 pr-4">${record.amountUsd.toFixed(2)}</td>
      <td className="py-3 pr-4 capitalize">{record.provider}</td>
      <td className="py-3">
        <Badge
          variant={record.status === 'completed' ? 'default' : 'secondary'}
          className={cn(
            record.status === 'completed' && 'border-green-500/20 bg-green-500/10 text-green-400',
            record.status === 'pending' && 'border-yellow-500/20 bg-yellow-500/10 text-yellow-400',
            record.status === 'failed' && 'border-red-500/20 bg-red-500/10 text-red-400'
          )}
        >
          {record.status}
        </Badge>
      </td>
    </tr>
  );
}

function PaymentHistory(): React.ReactElement {
  const { data: history, isLoading } = usePaymentHistory();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment History</CardTitle>
        <CardDescription>Your credit top-ups and transactions</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !history || history.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-gray-400">No payments yet.</p>
            <p className="mt-1 text-sm text-gray-500">Top up credits above to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] text-left text-gray-500">
                  <th className="pb-3 pr-4 font-medium">Date</th>
                  <th className="pb-3 pr-4 font-medium">Credits</th>
                  <th className="pb-3 pr-4 font-medium">Amount</th>
                  <th className="pb-3 pr-4 font-medium">Provider</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {history.map((rec) => (
                  <HistoryRow key={rec.id} record={rec} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function BillingPage(): React.ReactElement {
  const { data: packages, isLoading: packagesLoading } = useCreditPackages();
  const stripeCheckout = useStripeCheckout();
  const solanaPayment = useSolanaPayment();
  const [activeSolanaPayment, setActiveSolanaPayment] = useState<SolanaPaymentResult | null>(null);
  const [activeStripePackage, setActiveStripePackage] = useState<string | null>(null);
  const [activeSolanaPackage, setActiveSolanaPackage] = useState<string | null>(null);

  const handleStripe = async (packageId: string): Promise<void> => {
    setActiveStripePackage(packageId);
    try {
      await stripeCheckout.mutateAsync(packageId);
    } catch (err) {
      toast({ title: 'Checkout failed', description: String(err), variant: 'destructive' });
    } finally {
      setActiveStripePackage(null);
    }
  };

  const handleSolana = async (packageId: string): Promise<void> => {
    setActiveSolanaPackage(packageId);
    try {
      const result = await solanaPayment.mutateAsync(packageId);
      setActiveSolanaPayment(result);
    } catch (err) {
      toast({
        title: 'Failed to create payment',
        description: String(err),
        variant: 'destructive',
      });
    } finally {
      setActiveSolanaPackage(null);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white">Billing</h1>
        <p className="mt-1 text-gray-400">
          Manage your API credits. Free tier: 1,000 calls/month — beyond that, pay-as-you-go.
        </p>
      </div>

      <BalanceCard />

      <div>
        <h2 className="mb-4 text-xl font-semibold text-white">Top Up Credits</h2>
        {packagesLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-52 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(packages ?? []).map((pkg) => (
              <PackageCard
                key={pkg.id}
                pkg={pkg}
                onStripe={() => void handleStripe(pkg.id)}
                onSolana={() => void handleSolana(pkg.id)}
                stripeLoading={activeStripePackage === pkg.id}
                solanaLoading={activeSolanaPackage === pkg.id}
              />
            ))}
          </div>
        )}
      </div>

      <PaymentHistory />

      {activeSolanaPayment && (
        <SolanaPayModal
          payment={activeSolanaPayment}
          onClose={() => setActiveSolanaPayment(null)}
        />
      )}
    </div>
  );
}
